import { Marked, type Tokens } from 'marked';
import { Post } from '../types';

export interface EmailTemplateOptions {
  subject: string;
  preheader?: string;
  editorGreeting?: string;
  customMessage?: string; // Markdown da mensagem editorial
  posts: Post[];
  appUrl?: string;
  unsubscribeUrl?: string;
  editionNumber?: number | string;
  dateStr?: string;
}

// ---------------------------------------------------------------------------
// Constantes visuais (tudo inline: Gmail/Outlook ignoram ou cortam <style>)
// ---------------------------------------------------------------------------

const SITE_URL = 'https://aaaviation.com.br';
const CONTACT_EMAIL = 'contato@aaaviation.com.br';

const FONT = "Arial,Helvetica,sans-serif";
const C_TEXT = '#334155';
const C_DARK = '#0A192F';
const C_MUTED = '#64748B';
const C_LINK = '#1D4ED8';
const C_BORDER = '#E2E8F0';
const C_ACCENT = '#F59E0B';

const P_STYLE = `margin:0 0 14px 0;font-family:${FONT};font-size:15px;line-height:1.65;color:${C_TEXT};`;
const A_STYLE = `color:${C_LINK};text-decoration:underline;`;

const DEFAULT_GREETING = 'Prezados aviadores, mecânicos e especialistas em aviação,';
const DEFAULT_MESSAGE =
  'Bem-vindo à nova edição do **Briefing Semanal**. Reunimos os temas mais relevantes da semana em manutenção aeronáutica e segurança de voo.';

const CATEGORY_LABELS: Record<string, string> = {
  manutencao: 'Manutenção',
  carreira: 'Carreira',
  safety: 'Safety',
  curiosidades: 'Curiosidades'
};

// ---------------------------------------------------------------------------
// Utilitários
// ---------------------------------------------------------------------------

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cleanBase(appUrl?: string): string {
  const base = (appUrl || SITE_URL).trim().replace(/\/+$/, '');
  return /^https?:\/\//i.test(base) ? base : SITE_URL;
}

/**
 * Só aceita http(s) e mailto. Caminhos relativos ao site ("/post/x") viram
 * absolutos; qualquer outra coisa (javascript:, data:, vbscript:...) é descartada.
 */
export function safeEmailUrl(raw: string | undefined | null, base: string = SITE_URL): string | null {
  const href = String(raw ?? '').trim();
  if (!href) return null;
  if (/^(https?:|mailto:)/i.test(href)) return href;
  if (href.startsWith('/') && !href.startsWith('//')) return `${cleanBase(base)}${href}`;
  return null;
}

/** Remove markdown e HTML para gerar um resumo em texto puro. */
export function stripToPlainText(input: string): string {
  return String(input || '')
    .replace(/\r\n/g, '\n')
    .replace(/```[\s\S]*?```/g, ' ') // blocos de código
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ') // tags HTML
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // imagens
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> texto
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, '') // títulos
    .replace(/^[ \t]{0,3}>[ \t]?/gm, '') // citações
    .replace(/^\s*([-*+]|\d+[.)])\s+/gm, '') // marcadores de lista
    .replace(/^\s*([-*_]\s*){3,}$/gm, ' ') // linhas horizontais
    .replace(/(\*\*|__|\*|_|~~|`)/g, '') // ênfases e código inline
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:!?-]+$/, '')}…`;
}

function postSummary(post: Post): string {
  const excerpt = stripToPlainText(post.excerpt || '');
  if (excerpt) return truncate(excerpt, 240);
  return truncate(stripToPlainText(post.content || ''), 140);
}

function categoryLabel(slug?: string): string {
  const key = String(slug || '').trim();
  if (!key) return 'Aviação';
  if (CATEGORY_LABELS[key]) return CATEGORY_LABELS[key];
  const spaced = key.replace(/[-_]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function postUrl(post: Post, base: string): string {
  return `${cleanBase(base)}/post/${encodeURIComponent(post.slug || post.id)}`;
}

function headerTitle(editionNumber?: number | string): { title: string; label: string } {
  const label = String(editionNumber ?? '').trim();
  const num = typeof editionNumber === 'number' ? String(editionNumber) : label.match(/#\s*(\d+)/)?.[1];
  if (num) return { title: `Briefing Semanal #${num} · AAAviation`, label: '' };
  return { title: 'Briefing Semanal · AAAviation', label };
}

// ---------------------------------------------------------------------------
// Markdown -> HTML de e-mail (marked, com HTML cru do autor ESCAPADO)
// ---------------------------------------------------------------------------

function createEmailMarked(base: string): Marked {
  const md = new Marked({ gfm: true, breaks: true });
  md.use({
    renderer: {
      // HTML cru digitado pelo autor aparece como texto, nunca é interpretado.
      html(token: Tokens.HTML | Tokens.Tag) {
        const escaped = escapeHtml(token.text);
        return 'block' in token && token.block ? `<p style="${P_STYLE}">${escaped.trim()}</p>` : escaped;
      },
      heading({ tokens, depth }: Tokens.Heading) {
        const size = depth <= 1 ? 20 : depth === 2 ? 18 : 16;
        return `<p style="margin:20px 0 10px 0;font-family:${FONT};font-size:${size}px;line-height:1.35;font-weight:bold;color:${C_DARK};">${this.parser.parseInline(tokens)}</p>`;
      },
      paragraph({ tokens }: Tokens.Paragraph) {
        return `<p style="${P_STYLE}">${this.parser.parseInline(tokens)}</p>`;
      },
      blockquote({ tokens }: Tokens.Blockquote) {
        const inner = this.parser.parse(tokens).replace(/margin:0 0 14px 0;/g, 'margin:0 0 6px 0;');
        return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 14px 0;"><tr><td style="border-left:4px solid ${C_LINK};background-color:#F8FAFC;padding:10px 14px;font-style:italic;">${inner}</td></tr></table>`;
      },
      list(token: Tokens.List) {
        const tag = token.ordered ? 'ol' : 'ul';
        const start = token.ordered && token.start !== 1 && token.start !== '' ? ` start="${Number(token.start) || 1}"` : '';
        const items = token.items.map(item => this.listitem(item)).join('');
        return `<${tag}${start} style="margin:0 0 14px 0;padding-left:22px;font-family:${FONT};font-size:15px;line-height:1.6;color:${C_TEXT};">${items}</${tag}>`;
      },
      listitem(item: Tokens.ListItem) {
        const inner = this.parser.parse(item.tokens).replace(/margin:0 0 14px 0;/g, 'margin:0 0 4px 0;');
        return `<li style="margin:0 0 6px 0;">${inner}</li>`;
      },
      checkbox({ checked }: Tokens.Checkbox) {
        return checked ? '☑ ' : '☐ ';
      },
      hr() {
        return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid ${C_BORDER};font-size:0;line-height:0;height:16px;">&nbsp;</td></tr></table>`;
      },
      strong({ tokens }: Tokens.Strong) {
        return `<strong style="color:${C_DARK};">${this.parser.parseInline(tokens)}</strong>`;
      },
      em({ tokens }: Tokens.Em) {
        return `<em>${this.parser.parseInline(tokens)}</em>`;
      },
      del({ tokens }: Tokens.Del) {
        return `<del>${this.parser.parseInline(tokens)}</del>`;
      },
      codespan({ text }: Tokens.Codespan) {
        return `<code style="font-family:Consolas,Menlo,monospace;font-size:13px;background-color:#F1F5F9;padding:1px 4px;">${escapeHtml(text)}</code>`;
      },
      code({ text, escaped }: Tokens.Code) {
        const body = escaped ? text : escapeHtml(text);
        return `<pre style="margin:0 0 14px 0;padding:12px;background-color:#F1F5F9;font-family:Consolas,Menlo,monospace;font-size:13px;line-height:1.5;color:${C_DARK};white-space:pre-wrap;word-break:break-word;">${body}</pre>`;
      },
      link({ href, title, tokens }: Tokens.Link) {
        const inner = this.parser.parseInline(tokens);
        const safe = safeEmailUrl(href, base);
        if (!safe) return inner;
        const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
        return `<a href="${escapeHtml(safe)}"${titleAttr} target="_blank" style="${A_STYLE}">${inner}</a>`;
      },
      // E-mail sem imagens: a imagem vira só o texto alternativo.
      image({ text }: Tokens.Image) {
        return text ? `<em>[${escapeHtml(text)}]</em>` : '';
      },
      table(token: Tokens.Table) {
        const cell = (c: Tokens.TableCell, head: boolean) => {
          const tag = head ? 'th' : 'td';
          const align = c.align ? `text-align:${c.align};` : 'text-align:left;';
          const weight = head ? 'font-weight:bold;background-color:#F8FAFC;' : '';
          return `<${tag} style="border:1px solid ${C_BORDER};padding:6px 8px;${align}${weight}">${this.parser.parseInline(c.tokens)}</${tag}>`;
        };
        const headRow = `<tr>${token.header.map(c => cell(c, true)).join('')}</tr>`;
        const bodyRows = token.rows.map(r => `<tr>${r.map(c => cell(c, false)).join('')}</tr>`).join('');
        return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 14px 0;border-collapse:collapse;font-family:${FONT};font-size:14px;color:${C_TEXT};">${headRow}${bodyRows}</table>`;
      }
    }
  });
  return md;
}

/** Converte o markdown do autor em HTML seguro com estilos inline (blocos). */
export function markdownToEmailHtml(markdown: string, appUrl: string = SITE_URL): string {
  const src = String(markdown || '').replace(/\r\n/g, '\n').trim();
  if (!src) return '';
  return (createEmailMarked(cleanBase(appUrl)).parse(src, { async: false }) as string).trim();
}

/** Mesma conversão, só para uma linha (saudação): sem parágrafos/blocos. */
export function markdownInlineToEmailHtml(markdown: string, appUrl: string = SITE_URL): string {
  const src = String(markdown || '').replace(/\s+/g, ' ').trim();
  if (!src) return '';
  return (createEmailMarked(cleanBase(appUrl)).parseInline(src, { async: false }) as string).trim();
}

/** Markdown -> texto legível (para a parte text/plain do e-mail). */
function markdownToPlainText(markdown: string, appUrl: string): string {
  const base = cleanBase(appUrl);
  return String(markdown || '')
    .replace(/\r\n/g, '\n')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, (_m, alt) => (alt ? `[${alt}]` : ''))
    .replace(/\[([^\]]*)\]\(([^)\s]*)[^)]*\)/g, (_m, text, href) => {
      const safe = safeEmailUrl(href, base);
      return safe ? `${text} (${safe.replace(/^mailto:/i, '')})` : text;
    })
    .replace(/<[^>]+>/g, '')
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+(.*)$/gm, (_m, t) => String(t).toUpperCase())
    .replace(/(\*\*|__|~~)/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------
// LEGADO: conversor regex antigo. Mantido sem alterações porque pode ser usado
// por outros e-mails (ex.: entrega do material/lead). O Briefing não usa mais.
// ATENÇÃO: não valida o esquema dos links nem escapa aspas no href.
// ---------------------------------------------------------------------------
/** @deprecated Use markdownToEmailHtml. */
export function formatMarkdownForEmail(text: string): string {
  if (!text) return '';

  let html = text
    // Normalize line endings
    .replace(/\r\n/g, '\n')
    // Escape HTML special chars
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Markdown Headers
  html = html.replace(/^### (.*$)/gim, '<h4 style="margin: 16px 0 8px 0; font-size: 15px; font-weight: 700; color: #0A192F; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif;">$1</h4>');
  html = html.replace(/^## (.*$)/gim, '<h3 style="margin: 18px 0 10px 0; font-size: 17px; font-weight: 800; color: #0A192F; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif;">$1</h3>');
  html = html.replace(/^# (.*$)/gim, '<h2 style="margin: 22px 0 12px 0; font-size: 19px; font-weight: 800; color: #0A192F; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif;">$1</h2>');

  // Blockquotes (e.g. > Doutrina de segurança...)
  html = html.replace(/^\> (.*$)/gim, '<div style="margin: 14px 0; padding: 10px 16px; background-color: #F8FAFC; border-left: 4px solid #1D4ED8; border-radius: 4px; font-style: italic; color: #334155; font-size: 13.5px; line-height: 1.6;">$1</div>');

  // Bold & Italic
  html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong style="color: #0A192F;">$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em style="color: #1E293B;">$1</em>');

  // Links [Title](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" style="color: #1D4ED8; font-weight: 600; text-decoration: underline;">$1</a>');

  // Unordered list items (- Item or * Item)
  html = html.replace(/^\s*[-*]\s+(.*$)/gim, '<li style="margin-bottom: 6px; padding-left: 4px; color: #334155; font-size: 14px; line-height: 1.6;">$1</li>');

  // Wrap loose <li> in <ul>
  html = html.replace(/(<li.*<\/li>(\s*<li.*<\/li>)*)/gim, '<ul style="margin: 12px 0 16px 0; padding-left: 20px; list-style-type: square; color: #1D4ED8;">$1</ul>');

  // Paragraphs (split by double linebreaks)
  const paragraphs = html.split(/\n\n+/);
  const formattedParagraphs = paragraphs.map(p => {
    p = p.trim();
    if (!p) return '';
    if (p.startsWith('<h') || p.startsWith('<div') || p.startsWith('<ul')) {
      return p;
    }
    // Convert remaining single linebreaks to <br/>
    const inner = p.replace(/\n/g, '<br/>');
    return `<p style="margin: 0 0 14px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 14.5px; line-height: 1.7; color: #334155;">${inner}</p>`;
  });

  return formattedParagraphs.filter(Boolean).join('');
}

// ---------------------------------------------------------------------------
// Template HTML (leve, sem imagens, sem <style>, tabelas + estilos inline)
// ---------------------------------------------------------------------------

function unsubscribeHref(unsubscribeUrl?: string): string {
  // Só usa a URL recebida se for http(s)/mailto de verdade; "#" e vazio caem no mailto.
  const given = unsubscribeUrl && unsubscribeUrl !== '#' ? safeEmailUrl(unsubscribeUrl) : null;
  return (
    given ||
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Remover do Briefing Semanal')}&body=${encodeURIComponent(
      'Por favor, removam meu e-mail da lista do Briefing Semanal da AAAviation.'
    )}`
  );
}

/**
 * Gera o HTML do Briefing Semanal: só texto e links, estilos inline,
 * compatível com Gmail/Outlook e bem abaixo do limite de corte do Gmail (~102 KB).
 * As capas dos posts (post.coverImage) são ignoradas de propósito.
 */
export function generateBriefingHtml({
  subject,
  preheader = 'Os principais destaques em manutenção aeronáutica e segurança de voo.',
  editorGreeting = DEFAULT_GREETING,
  customMessage,
  posts,
  appUrl = SITE_URL,
  unsubscribeUrl,
  editionNumber = 'Edição Semanal',
  dateStr = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
}: EmailTemplateOptions): string {
  const base = cleanBase(appUrl);
  const { title, label } = headerTitle(editionNumber);
  const subline = [label, dateStr].filter(Boolean).map(escapeHtml).join(' · ');

  const greetingHtml = markdownInlineToEmailHtml(editorGreeting || '', base);
  const messageHtml = markdownToEmailHtml(customMessage && customMessage.trim() ? customMessage : DEFAULT_MESSAGE, base);

  const articlesHtml = posts.length
    ? posts
        .map((post, index) => {
          const url = escapeHtml(postUrl(post, base));
          const meta = [categoryLabel(post.category), post.technicalBadge, `${post.readTimeMinutes || 5} min de leitura`]
            .filter(Boolean)
            .map(escapeHtml)
            .join(' · ');
          const summary = postSummary(post);
          const divider = index < posts.length - 1 ? `border-bottom:1px solid ${C_BORDER};` : '';
          return `<tr><td style="padding:16px 0;${divider}">
<p style="margin:0 0 4px 0;font-family:${FONT};font-size:11px;line-height:1.4;letter-spacing:1px;text-transform:uppercase;color:${C_MUTED};">${meta}</p>
<p style="margin:0 0 6px 0;font-family:${FONT};font-size:17px;line-height:1.35;font-weight:bold;"><a href="${url}" target="_blank" style="color:${C_DARK};text-decoration:none;">${escapeHtml(post.title)}</a></p>
${summary ? `<p style="margin:0 0 8px 0;font-family:${FONT};font-size:14px;line-height:1.6;color:#475569;">${escapeHtml(summary)}</p>` : ''}
<p style="margin:0;font-family:${FONT};font-size:14px;font-weight:bold;"><a href="${url}" target="_blank" style="${A_STYLE}">Ler o artigo &rarr;</a></p>
</td></tr>`;
        })
        .join('\n')
    : `<tr><td style="padding:16px 0;font-family:${FONT};font-size:14px;color:${C_MUTED};">Nenhum artigo selecionado para esta edição.</td></tr>`;

  // Preenchimento invisível após o preheader: evita que o cliente de e-mail
  // complete a prévia da caixa de entrada com o texto do corpo.
  const preheaderPad = '&#847;&zwnj;&nbsp;'.repeat(40);

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:#F1F5F9;">
<div style="display:none;max-height:0;max-width:0;overflow:hidden;opacity:0;font-size:1px;line-height:1px;color:#F1F5F9;">${escapeHtml(preheader)}${preheaderPad}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F1F5F9;">
<tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;background-color:#FFFFFF;border:1px solid ${C_BORDER};">
<tr><td style="background-color:${C_DARK};border-bottom:3px solid ${C_ACCENT};padding:26px 28px;">
<p style="margin:0 0 6px 0;font-family:${FONT};font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#FBBF24;font-weight:bold;">Alexandre Andrade · AAAviation</p>
<h1 style="margin:0;font-family:${FONT};font-size:22px;line-height:1.3;font-weight:bold;color:#FFFFFF;">${escapeHtml(title)}</h1>
${subline ? `<p style="margin:6px 0 0 0;font-family:${FONT};font-size:13px;color:#93C5FD;">${subline}</p>` : ''}
</td></tr>
<tr><td style="padding:28px 28px 8px 28px;">
${greetingHtml ? `<p style="margin:0 0 14px 0;font-family:${FONT};font-size:16px;line-height:1.5;font-weight:bold;color:${C_DARK};">${greetingHtml}</p>` : ''}
${messageHtml}
</td></tr>
<tr><td style="padding:8px 28px 20px 28px;">
<p style="margin:0;padding:0 0 8px 0;border-bottom:2px solid ${C_DARK};font-family:${FONT};font-size:13px;letter-spacing:1px;text-transform:uppercase;font-weight:bold;color:${C_DARK};">Nesta edição</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
${articlesHtml}
</table>
</td></tr>
<tr><td style="padding:22px 28px;background-color:#F8FAFC;border-top:1px solid ${C_BORDER};">
<p style="margin:0 0 4px 0;font-family:${FONT};font-size:14px;line-height:1.5;color:${C_DARK};font-weight:bold;">Alexandre Andrade, AAAviation</p>
<p style="margin:0 0 14px 0;font-family:${FONT};font-size:13px;line-height:1.5;color:${C_TEXT};">Manutenção aeronáutica e segurança de voo · <a href="${escapeHtml(base)}" target="_blank" style="${A_STYLE}">aaaviation.com.br</a></p>
<p style="margin:0;font-family:${FONT};font-size:11px;line-height:1.6;color:${C_MUTED};">Você recebe este briefing porque se inscreveu no portal AAAviation. Para sair da lista, <a href="${escapeHtml(unsubscribeHref(unsubscribeUrl))}" target="_blank" style="color:${C_MUTED};text-decoration:underline;">peça a remoção por e-mail</a> (${CONTACT_EMAIL}).</p>
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

/** Versão texto puro do mesmo briefing (campo "text" do Resend). */
export function generateBriefingText({
  editorGreeting = DEFAULT_GREETING,
  customMessage,
  posts,
  appUrl = SITE_URL,
  unsubscribeUrl,
  editionNumber = 'Edição Semanal',
  dateStr = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
}: EmailTemplateOptions): string {
  const base = cleanBase(appUrl);
  const { title, label } = headerTitle(editionNumber);
  const unsub = unsubscribeHref(unsubscribeUrl);
  const unsubText = unsub.startsWith('mailto:')
    ? `escreva para ${CONTACT_EMAIL} com o assunto "Remover do Briefing Semanal"`
    : `acesse ${unsub}`;

  const articles = posts.length
    ? posts
        .map(post => {
          const meta = [categoryLabel(post.category), post.technicalBadge, `${post.readTimeMinutes || 5} min`]
            .filter(Boolean)
            .join(' · ');
          const summary = postSummary(post);
          return [`* ${stripToPlainText(post.title)}`, `  ${meta}`, summary ? `  ${summary}` : '', `  ${postUrl(post, base)}`]
            .filter(Boolean)
            .join('\n');
        })
        .join('\n\n')
    : 'Nenhum artigo selecionado para esta edição.';

  return [
    title.toUpperCase(),
    [label, dateStr].filter(Boolean).join(' · '),
    '',
    markdownToPlainText(editorGreeting || '', base),
    '',
    markdownToPlainText(customMessage && customMessage.trim() ? customMessage : DEFAULT_MESSAGE, base),
    '',
    'NESTA EDIÇÃO',
    '------------',
    articles,
    '',
    '--',
    'Alexandre Andrade, AAAviation',
    base,
    '',
    `Para sair da lista, ${unsubText}.`
  ]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Tamanho em bytes (UTF-8) — é o que conta para o corte do Gmail. */
export function byteSize(value: string): number {
  return new TextEncoder().encode(value || '').length;
}
